/**
 * SPIKE (owner, Sep 10): roads as **paths** instead of lines on a lattice.
 *
 * Today `RoadNet` answers "is there a road here" with `linePos(level, k) = k * SPACING[level]` — every road is a
 * straight, axis-aligned line, and an arc, an S-bend, a roundabout or a slip road has nowhere to live. This module is
 * the alternative, built to be looked at before anything commits to it (docs/DESIGN_MAPGEN.md, stage 2).
 *
 * The property that must survive is the one that makes an endless multiplayer world possible: **any chunk answers from
 * the seed alone**, without asking a neighbour. So:
 *
 *   - The world is cut into node cells of `CELL` tiles. Cell (cx, cy) owns exactly one **node**, whose position is a
 *     hash of the cell, jittered inside it — barely, in a town (so towns read as grids), freely out in the country.
 *   - A node is nudged off water and off steep ground by trying a few deterministic candidate spots and keeping the
 *     cheapest. No search, no relaxation, no neighbour talk.
 *   - Each cell owns two **edges**: east and south. Whether one exists is a hash against the density there, so a city
 *     gets a full mesh and the country gets a sparse net of the roads that actually go somewhere.
 *   - An edge is a quadratic Bézier. Its bend comes from a deterministic wobble *plus* a terrain term: the midpoint
 *     looks left and right, and the curve leans towards whichever side is cheaper to build on. That is what makes a
 *     road sweep around a lake instead of jagging through it.
 *
 * Everything is cached per cell, and a query only ever touches the 3×3 cells around the point.
 */
import type { Fields } from './fields';
import type { Relief } from './relief';
import { hash01 } from './rng';
import { OVER_FLAT } from './roads';
import { fbm } from './noise';
import { StreetNames } from './street-names';

/**
 * The net has **levels**, like the lattice it replaces: nodes live on one fine grid so that every level shares them
 * (a trunk road and a side street meet at the *same* point, which is what makes junctions possible at all), and an
 * edge of level L joins nodes L-aligned to it. A level exists where the ground is built-up enough to justify it, so
 * the country gets the trunk roads that go somewhere and a city gets the full mesh.
 */
export const CELL = 64;
/** cells stepped over per level: 8 = 512 tiles (trunk), then 256, 128, 64 */
export const LEVEL_STEP = [8, 4, 2, 1] as const;
/** a level only appears where density reaches this */
const LEVEL_DENSITY = [0, 0.03, 0.12, 0.3] as const;
/** and then only this often (the trunk is always there; local streets are near-certain in a city) */
const LEVEL_CHANCE = [0.82, 0.78, 0.88, 0.95] as const;
/** how far a node may sit from its cell's centre, as a share of the cell (town … country) */
const JITTER_TOWN = 0.05, JITTER_WILD = 0.30;
/** how hard an edge bends, as a share of its length (town … country) */
const BEND_TOWN = 0.02, BEND_WILD = 0.16;
/** how far the midpoint looks to either side when deciding which way to lean, in tiles */
const LOOK = 26;
/** samples across a block in a cached cost grid, in each direction */
const GRID = 8;
/** the block a cost grid covers, by band: trunk roads (levels 0–1) read the coarse one, local roads the fine one */
const BAND_BLOCK = [CELL * 8, CELL] as const;
/** points a curve is sampled into for distance queries (per edge, cached) */
const STEPS = 14;

export type PathClass = 'highway' | 'avenue' | 'street' | 'lane' | 'dirt' | 'rail';
/** the deck height of a grade separation and its ramp, the lattice's own numbers (roads.ts OVER_H / OVER_RAMP) */
export const OVER_H = 5, OVER_RAMP = 22, OVER_CHANCE = 0.9;
/** how far a railway's centre line may wander from its cell line, in tiles — smooth over ~900 tiles, so a train can take it */
const RAIL_SWAY = 10;
/**
 * (Q82 b, owner Sep 11) the railway is a layer of its own beside the road, not the road's line: its centre runs half a
 * cell out from the line that carries it, on the side away from the corridor — mid-block, so the line keeps its
 * avenue (Brightford keeps its lake crossing), and a town's roads, which barely jitter, stay clear of the track.
 */
const RAIL_OFF = CELL / 2;
/** the most cells a named road runs before it is allowed a new name (a walk bound, and a long road does change name) */
const RUN_MAX = 48;
/** how far past its end a player's piece will reach for a generated road's kerb (tiles) — a junction box, not a street */
export const ATTACH_REACH = 12;
/**
 * How many of an edge's `STEPS` samples may be water before the road is not built: what a road of that class is
 * willing to carry on a deck. A motorway spans most of a lake; a street a river; a lane a stream; a dirt track nothing.
 */
export const MAX_DECK: Record<PathClass, number> = { highway: STEPS, avenue: 10, street: 4, lane: 2, dirt: 0, rail: STEPS };
// (§43, Phase B) a motorway spans the whole lake and an avenue most of one: a trunk road refused for a long crossing left
// the network ending on a shore with the deck "stopping in the air" (owner, Sep 10) — a long bridge is what a trunk does.
/** half-widths in tiles, by class — the spike's own numbers, wider than today's roads on purpose (owner) */
export const HALF_WIDTH: Record<PathClass, number> = { highway: 7, avenue: 5, street: 3.6, lane: 2.6, dirt: 2.2, rail: 1.5 };

export interface PathNode { x: number; y: number; cx: number; cy: number; /** 0 = wilderness, 1 = city core */ density: number; /** standing in water after every candidate spot was tried: no road may end here, since a junction in a lake is not a junction */ wet: boolean }
/** one road leaving a junction: the edge, which way along it the junction sits, and the axis the edge reports */
export interface Arm { edge: PathEdge; /** +1: the junction is the edge's start (a), the road runs away towards b; −1: the reverse */ away: 1 | -1; axis: 'v' | 'h' }
/** a junction as an object: a node with the roads that meet there (track 2's §21 — what the lights and cameras need) */
export interface Junction { x: number; y: number; cx: number; cy: number; arms: Arm[]; /** a grade-separated interchange: the axis carried over the other on a deck (plan B: "interchanges kilometres apart") */ over?: 'v' | 'h' }
/** how far apart interchanges are along a trunk line, in cells (16 × 64 = a kilometre), and how many of those crossings get one */
const INTERCHANGE_EVERY = 16, INTERCHANGE_AT = 4, INTERCHANGE_CHANCE = 0.7;
/** a stretch of an edge carried on a deck over water, as a span of the curve's parameter, deck ends on dry ground */
export interface Deck { t0: number; t1: number }
/** a grade separation: this edge passes *over* `under` on a flat deck between t0 and t1, with ramps outside the span */
export interface Over { t0: number; t1: number; under: PathEdge }
export interface PathEdge {
  a: PathNode; b: PathNode;
  cls: PathClass;
  /** 0 = the trunk between towns, 3 = a local street */
  level: number;
  /** the Bézier control point */
  qx: number; qy: number;
  /** (owner, Sep 24: smooth roads) a cubic's two handles, when the network smooths its roads through the nodes: the curve is then a cubic from `a` to `b` */
  c?: [number, number, number, number];
  /** the sampled curve, for distance queries */
  pts: Float64Array;
  /** bounds of the curve *plus* its half-width — a query outside this box cannot be on this road */
  x0: number; y0: number; x1: number; y1: number;
  /** the edge's own key, stable across chunks: the owning cell and the direction */
  key: string;
  /** bridges: where this road crosses water on a deck (none on most roads) */
  decks?: Deck[];
  /** grade separations: where this edge is carried over another (a railway over a street, a motorway over an avenue);
   *  filled in by `resolve` once the neighbouring cells exist, `undefined` until then and `[]` when there are none */
  overs?: Over[];
}
/** an edge's quadratic before any smoothing: what a neighbour's smoothing reads */
interface Quad { a: PathNode; b: PathNode; level: number; cls: PathClass; qx: number; qy: number; pts: Float64Array }
export interface PathHit { edge: PathEdge; /** distance from the centre line, tiles */ d: number; /** 0..1 along the edge */ t: number }

/** a cell key that is a number, not a string: this is on the per-tile path and string keys allocate */
const cellKey = (cx: number, cy: number): number => (cx & 0x3fffff) * 0x400000 + (cy & 0x3fffff);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** (Sep 24) a cubic Bézier from a to b with handles c1 and c2, sampled into STEPS+1 points */
function cubicCurve(a: PathNode, c1x: number, c1y: number, c2x: number, c2y: number, b: PathNode): Float64Array {
  const pts = new Float64Array((STEPS + 1) * 2);
  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS, u = 1 - t, w0 = u * u * u, w1 = 3 * u * u * t, w2 = 3 * u * t * t, w3 = t * t * t;
    pts[i * 2] = w0 * a.x + w1 * c1x + w2 * c2x + w3 * b.x;
    pts[i * 2 + 1] = w0 * a.y + w1 * c1y + w2 * c2y + w3 * b.y;
  }
  return pts;
}
/** the point of an edge's curve at parameter t (0 at a, 1 at b): its cubic when smoothed, else its quadratic */
export function edgeAt(e: PathEdge, t: number): [number, number] {
  const u = 1 - t;
  if (e.c) { const w0 = u * u * u, w1 = 3 * u * u * t, w2 = 3 * u * t * t, w3 = t * t * t; return [w0 * e.a.x + w1 * e.c[0] + w2 * e.c[2] + w3 * e.b.x, w0 * e.a.y + w1 * e.c[1] + w2 * e.c[3] + w3 * e.b.y]; }
  return [u * u * e.a.x + 2 * u * t * e.qx + t * t * e.b.x, u * u * e.a.y + 2 * u * t * e.qy + t * t * e.b.y];
}
/** a quadratic Bézier from a to b through the control point, sampled into STEPS+1 points */
function curve(a: PathNode, b: PathNode, qx: number, qy: number): Float64Array {
  const pts = new Float64Array((STEPS + 1) * 2);
  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS, u = 1 - t;
    pts[i * 2] = u * u * a.x + 2 * u * t * qx + t * t * b.x;
    pts[i * 2 + 1] = u * u * a.y + 2 * u * t * qy + t * t * b.y;
  }
  return pts;
}

export class PathNet {
  private nodes = new Map<number, PathNode>();
  private edges = new Map<number, PathEdge[]>();
  private buckets = new Map<number, PathEdge[]>();
  private grids = new Map<number, Float32Array>();
  /** which side of its corridor the railway on a fine edge's line lies (0: none) — see `railSide` */
  private rails = new Map<number, -1 | 0 | 1>();
  private roadKeys = new Map<string, string>();
  private names?: StreetNames;

  /**
   * `relief` is what makes the two spikes one system: without it the network costs its ground by the raw noise and
   * drives straight over a mountain because the mountain is not in that number. With it, a road is priced by the
   * grade it would actually have to climb.
   */
  /** (owner, Sep 24: "very sharp turns where two roads meet — the roads should start to curve earlier") `smooth`: a road keeps its direction through a node (a world's generation option: it moves the lots) */
  constructor(readonly fields: Fields, readonly seed: number, readonly relief?: Relief, readonly opts: { smooth?: boolean } = {}) {}

  /** How built-up it is around a point, 0..1 — the one number that shapes everything else here. */
  private urban(x: number, y: number): number {
    return Math.max(0, Math.min(1, this.fields.density(x, y)));
  }

  /**
   * How *laid out* a place is, 0..1 — the same density, but as a judgement rather than a gradient. A town was
   * surveyed and its streets were cut to a plan; the country followed the ground. Smoothstepped around a third
   * density so the edge of a town is a short transition and not a half-planned smear (owner: "roads in cities/towns
   * can be grid-like, but in other areas roads should go like they do in the real world").
   */
  private planned(d: number): number {
    const t = Math.max(0, Math.min(1, (d - 0.12) / 0.3));
    return t * t * (3 - 2 * t);
  }

  /**
   * What it costs to put a road at a point: water is close to impossible, steep ground is dear, and the cost rises
   * smoothly rather than in steps so a curve can be pushed gently instead of snapping.
   */
  cost(x: number, y: number): number {
    const f = this.fields;
    const e = f.elevation(x, y);
    if (e < f.seaLevel) return 40 + (f.seaLevel - e) * 60; // water: go round unless there is no way round
    const slope = this.slope(x, y);
    const t = f.terrainAt(x, y, true);
    const wood = t.id === 'forest' ? 3 : 0; // trees are cheap to clear, but a road would rather not
    // A gentle rise is nothing; a steep one is most of what a road costs. Squared, so the price of a hillside grows
    // faster than the hillside does, which is what sends a lane round a hill and a motorway through it (owner:
    // "small rural roads … it's more cost effective to go around an obstacle or just make the road climb a hill").
    const steep = this.relief ? slope * 8 + slope * slope * 90 : slope * 55;
    return 1 + steep + wood;
  }

  /**
   * The cost of the ground across one cell, sampled on a coarse grid and cached.
   *
   * Scoring five candidate curves for every edge against a live terrain query cost **1005 ms** for the first chunk of
   * a fresh area — against a whole-chunk budget of about 64 ms for everything the generator does today. The samples
   * were nearly all repeats: a handful of cells, asked thousands of times, at points a few tiles apart. A grid of
   * `GRID + 1` squared samples per cell, read back bilinearly, answers the same question from a few hundred
   * evaluations instead of tens of thousands, and the ground does not change enough over eight tiles to notice.
   */
  private grid(band: 0 | 1, cx: number, cy: number): Float32Array {
    const key = cellKey(cx, cy) * 2 + band;
    let g = this.grids.get(key);
    if (g) return g;
    if (this.grids.size > 20000) this.grids.clear();
    const block = BAND_BLOCK[band];
    const step = block / GRID, w = GRID + 3; // one ring of samples beyond the block, so the slope at the edge has neighbours
    const h = new Float32Array(w * w);
    const r = this.relief;
    for (let j = 0; j < w; j++) {
      for (let i = 0; i < w; i++) {
        const x = cx * block + (i - 1) * step, y = cy * block + (j - 1) * step;
        h[j * w + i] = r ? r.smooth(x, y, true) : 0;
      }
    }
    // the cost falls out of the height grid: the slope is the difference between neighbours, which is free once the
    // heights are there, and the water and the woods are one cheap sample each
    g = new Float32Array((GRID + 1) * (GRID + 1));
    for (let j = 0; j <= GRID; j++) {
      for (let i = 0; i <= GRID; i++) {
        const x = cx * block + i * step, y = cy * block + j * step;
        const hi = (j + 1) * w + (i + 1);
        const slope = r ? Math.hypot((h[hi + 1] - h[hi - 1]) / (2 * step), (h[hi + w] - h[hi - w]) / (2 * step)) : 0;
        const wet = r ? r.wetFast(x, y) : this.fields.elevationRaw(x, y) < this.fields.seaLevel;
        const wood = this.fields.terrainAt(x, y, true).id === 'forest' ? 3 : 0;
        g[j * (GRID + 1) + i] = wet ? 60 : 1 + slope * 8 + slope * slope * 90 + wood;
      }
    }
    this.grids.set(key, g);
    return g;
  }

  /**
   * What the ground costs here, read off the cached grid for the scale being routed.
   *
   * The scale matters as much as the caching. A trunk edge spans 512 tiles, and pricing it against the *fine* grid
   * meant building a 64-tile grid for every cell it passed over — a hundred grids for one chunk. A motorway does not
   * care about an eight-tile dip, so it reads a grid of the same shape stretched over its own block instead.
   */
  costAt(x: number, y: number, band: 0 | 1 = 1): number {
    const block = BAND_BLOCK[band];
    const cx = Math.floor(x / block), cy = Math.floor(y / block);
    const g = this.grid(band, cx, cy);
    const step = block / GRID;
    const fx = (x - cx * block) / step, fy = (y - cy * block) / step;
    const i = Math.max(0, Math.min(GRID - 1, Math.floor(fx))), j = Math.max(0, Math.min(GRID - 1, Math.floor(fy)));
    const tx = fx - i, ty = fy - j, w = GRID + 1;
    const a = g[j * w + i], b = g[j * w + i + 1], c = g[(j + 1) * w + i], d = g[(j + 1) * w + i + 1];
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  }

  /** Steepness where the road would go: metres of climb per tile when the relief is known, the raw field otherwise. */
  slope(x: number, y: number): number {
    if (this.relief) return this.relief.grade(x, y).slope;
    const f = this.fields, d = 6;
    const ex = (f.elevation(x + d, y) - f.elevation(x - d, y)) / (2 * d);
    const ey = (f.elevation(x, y + d) - f.elevation(x, y - d)) / (2 * d);
    return Math.hypot(ex, ey);
  }

  /** The node of a cell: hashed inside the cell, then nudged onto the cheapest of a few candidate spots. */
  node(cx: number, cy: number): PathNode {
    const key = cellKey(cx, cy);
    let n = this.nodes.get(key);
    if (n) return n;
    if (this.nodes.size > 40000) this.nodes.clear();
    const midX = (cx + 0.5) * CELL, midY = (cy + 0.5) * CELL;
    const dens = this.urban(midX, midY);
    const laid = this.planned(dens);
    const j = lerp(JITTER_WILD, JITTER_TOWN, laid) * CELL;
    // Two displacements, and the difference between them is the difference between a network that *wanders* and one
    // that *flows*. The hash moves this node alone; the flow field is smooth over hundreds of tiles, so a whole row of
    // nodes leans the same way and the roads through them read as one long sweeping road rather than a chain of
    // unrelated bends. A town is engineered, so it barely feels either.
    const flowX = fbm(this.seed + 311, midX / 700, midY / 700, 2) - 0.5;
    const flowY = fbm(this.seed + 313, midX / 700, midY / 700, 2) - 0.5;
    const flow = lerp(1.7, 0.06, laid) * CELL;
    const bx = midX + (hash01(this.seed + 101, cx, cy) - 0.5) * 2 * j + flowX * flow;
    const by = midY + (hash01(this.seed + 103, cx, cy) - 0.5) * 2 * j + flowY * flow;
    // four deterministic candidates around that point; the cheapest wins (a node in a lake ruins every edge into it)
    // (§43, Phase B) and not on a shore either: a node a few tiles from water puts its junction box where a bridge's
    // ramp has to land, and a ramp cut off at a junction is a cliff — so water within a ramp's length of a candidate
    // counts against it, ring-sampled, and the driest candidate wins
    const shore = (x: number, y: number): number => { let wet = 0; for (let k = 0; k < 8; k++) { const a = (k / 8) * Math.PI * 2; if (this.fields.elevation(x + Math.cos(a) * 10, y + Math.sin(a) * 10) < this.fields.seaLevel) wet++; } return wet * 12; };
    let best = { x: bx, y: by, c: this.costAt(bx, by) + shore(bx, by) };
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + hash01(this.seed + 107, cx, cy) * Math.PI;
      const r = CELL * 0.22;
      const x = bx + Math.cos(a) * r, y = by + Math.sin(a) * r;
      const c = this.costAt(x, y) + shore(x, y);
      if (c < best.c) best = { x, y, c };
    }
    n = { x: best.x, y: best.y, cx, cy, density: dens, wet: this.fields.elevation(best.x, best.y) < this.fields.seaLevel }; // (§43) by the same blurred elevation the terrain uses, so a node is wet exactly when its tile is water
    this.nodes.set(key, n);
    return n;
  }

  /** a cell's node's density without placing the node — `node` stores exactly this (the cell middle's `urban`) */
  private cellDensity(cx: number, cy: number): number {
    const n = this.nodes.get(cellKey(cx, cy));
    return n ? n.density : this.urban((cx + 0.5) * CELL, (cy + 0.5) * CELL);
  }

  /**
   * The class of a road: its **level** decides, and how built-up it is decides the rest. A trunk road between towns is
   * a motorway; the same level through open country is the main road it becomes when there is nothing to carry.
   */
  private classOf(level: number, d: number, h: number): PathClass {
    if (level === 0) return d > 0.10 ? 'highway' : 'avenue';
    if (level === 1) return d > 0.30 ? 'avenue' : d > 0.03 ? 'street' : h < 0.5 ? 'lane' : 'dirt';
    if (level === 2) return d > 0.45 ? 'street' : d > 0.06 ? 'lane' : 'dirt';
    return d > 0.55 ? 'street' : h < 0.35 ? 'lane' : 'dirt';
  }

  /**
   * The level of the fine edge leaving cell (cx, cy) in `dir`, or -1 for no edge — the lattice's `isOwnLevel`, ported.
   *
   * Every edge is one cell long. A level-L *line* is a run of them along a row (east edges) or a column (south edges)
   * whose index is a multiple of `LEVEL_STEP[L]`, and it is claimed segment by segment: the segment between two
   * L-aligned nodes exists or not by one hash. A fine edge's level is the **coarsest** line that claims it, and its
   * class follows from that — a motorway is a chain of 64-tile edges promoted to highway, not a 512-tile curve of
   * its own. The first spike had both, and the junction test caught it: the trunk edge and the local edge left the
   * same node together and the first stretch was two roads on top of each other. The lattice never had that because
   * a coarse line *passes through* the fine nodes; a chain does too.
   */
  private levelOf(cx: number, cy: number, dir: 0 | 1, maxLevel = LEVEL_STEP.length - 1): number {
    // the row (east edges) or column (south edges) this edge lies on, and where along it
    const line = dir === 0 ? cy : cx, along = dir === 0 ? cx : cy;
    for (let level = 0; level <= maxLevel; level++) { // (maxLevel: the far map asks only for the big roads — their nodes are few, a street's is every cell's)
      const step = LEVEL_STEP[level];
      if (line % step !== 0) continue; // not a line of this level
      const seg = Math.floor(along / step) * step; // the L-aligned node the segment starts at
      const ax = dir === 0 ? seg : cx, ay = dir === 0 ? cy : seg;
      // (Sep 25, Area 69's probe) a node's density is its cell middle's, known before the node is placed: asked here for every cell
      // at every level, placing the node (its search off water) just to read it was most of what building the country cost
      if (LEVEL_DENSITY[level] > 0 && Math.max(this.cellDensity(ax, ay), dir === 0 ? this.cellDensity(ax + step, ay) : this.cellDensity(ax, ay + step)) < LEVEL_DENSITY[level]) continue;
      if (hash01(this.seed + (dir ? 211 : 223) + level * 17, ax, ay) <= LEVEL_CHANCE[level]) return level;
    }
    return -1;
  }

  /** How many roads meet at this node: its own east and south edges and the two coming into it. Four at most. */
  private degree(cx: number, cy: number): number {
    let n = 0;
    if (this.levelOf(cx, cy, 0) >= 0) n++;
    if (this.levelOf(cx, cy, 1) >= 0) n++;
    if (this.levelOf(cx - 1, cy, 0) >= 0) n++;
    if (this.levelOf(cx, cy - 1, 1) >= 0) n++;
    return n;
  }

  /**
   * Is the fine edge (cx, cy, dir) a railway? The same rule as the lattice's: `Settlements.onRail` says which 128-tile
   * segments of which level-1 lines carry a rail leg — the L beside about half the corridors, stretched past its town so
   * the dead end where a train turns is in the country (owner, Sep 7). Here a level-1 line is a row or column of
   * cells, so the leg is the chain of cell edges along it. Cached: `onRail` walks the corridors, and this is asked from
   * `levelOf`, which everything asks.
   */
  private railSide(cx: number, cy: number, dir: 0 | 1): -1 | 0 | 1 {
    const key = cellKey(cx, cy) * 2 + dir;
    let r = this.rails.get(key);
    if (r !== undefined) return r;
    if (this.rails.size > 80000) this.rails.clear();
    const axis = dir === 0 ? 'h' : 'v', pos = (dir === 0 ? cy : cx) * CELL, seg = Math.floor((dir === 0 ? cx : cy) * CELL / 128);
    r = this.fields.settlements.railSide(axis, pos, seg);
    this.rails.set(key, r);
    return r;
  }

  /**
   * Where the railway's centre line is at `along` on the line `line` (a row for `dir` 0, a column for 1): the cell line
   * plus a sway that is smooth over hundreds of tiles. A train cannot take the jitter a road node takes — the rail is
   * its own smooth curve, not a chain through the road nodes — and because the sway is one function of `along`, the
   * edges into and out of a rail node have the same tangent there: the line is C¹ without any edge knowing its neighbour.
   */
  private railLine(line: number, along: number, dir: 0 | 1, side: -1 | 1): [number, number] {
    const centre = (line + 0.5) * CELL + side * RAIL_OFF;
    const sway = (fbm(this.seed + (dir ? 331 : 337), along / 900, line * 7.3, 2) - 0.5) * 2 * RAIL_SWAY;
    return dir === 0 ? [along, centre + sway] : [centre + sway, along];
  }

  /** A railway edge: from one cell's rail point to the next along the line, bent through the line's own midpoint. */
  private makeRail(cx: number, cy: number, dir: 0 | 1, side: -1 | 1): PathEdge {
    const line = dir === 0 ? cy : cx, u0 = ((dir === 0 ? cx : cy) + 0.5) * CELL;
    const [ax, ay] = this.railLine(line, u0, dir, side), [bx, by] = this.railLine(line, u0 + CELL, dir, side), [mx, my] = this.railLine(line, u0 + CELL / 2, dir, side);
    const wetAt = (x: number, y: number) => this.fields.elevation(x, y) < this.fields.seaLevel;
    const a: PathNode = { x: ax, y: ay, cx, cy, density: this.urban(ax, ay), wet: wetAt(ax, ay) };
    const ncx = dir === 0 ? cx + 1 : cx, ncy = dir === 0 ? cy : cy + 1;
    const b: PathNode = { x: bx, y: by, cx: ncx, cy: ncy, density: this.urban(bx, by), wet: wetAt(bx, by) };
    // the quadratic through a, m, b: its control point is 2m − (a + b) / 2
    const qx = 2 * mx - (ax + bx) / 2, qy = 2 * my - (ay + by) / 2;
    const pts = curve(a, b, qx, qy);
    // water is a viaduct, whole: a railway does not go round, and it does not end in a lake — every wet run is a deck
    const sea = this.fields.seaLevel;
    let decks: Deck[] | undefined;
    for (let i = 0; i <= STEPS; i++) {
      if (this.fields.elevation(pts[i * 2], pts[i * 2 + 1]) >= sea) continue;
      let j = i; while (j <= STEPS && this.fields.elevation(pts[j * 2], pts[j * 2 + 1]) < sea) j++;
      (decks ??= []).push({ t0: Math.max(0, i - 1) / STEPS, t1: Math.min(STEPS, j) / STEPS });
      i = j;
    }
    const pad = HALF_WIDTH.rail + 1;
    let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
    for (let i = 0; i <= STEPS; i++) {
      const px = pts[i * 2], py = pts[i * 2 + 1];
      if (px < bx0) bx0 = px; if (px > bx1) bx1 = px;
      if (py < by0) by0 = py; if (py > by1) by1 = py;
    }
    return { a, b, cls: 'rail', level: 1, qx, qy, pts, x0: bx0 - pad, y0: by0 - pad, x1: bx1 + pad, y1: by1 + pad, key: `${cx},${cy},${dir},rail`, ...(decks ? { decks } : {}) };
  }

  /** The fine edge a cell owns in one direction, or nothing. Its level — and so its class — is whichever line claims it. */
  /**
   * (owner, Sep 24: "very sharp turns where two roads meet at maybe 45° — the roads should start to curve earlier to make the turn softer")
   * With `smooth` on, an edge is a cubic whose ends leave each node in the direction the road keeps through it: the
   * average of the way it came in and the way it goes on (the same line's next edge, or at a corner — a node with only
   * two roads — the other one), capped at 44° from the axis the adapter gives the edge (its chord's: the lane maths
   * needs the curve within 45° of it). Both edges at a node compute the same direction from the same two quadratics, so the road is smooth
   * across the node with no edge asking a neighbour anything but its (memoised) quadratic. A dead end, a T's stem and a
   * crossing's roads each keep their own. The handles are the quadratic's own lengths: the bend round a lake stays.
   */
  private makeEdge(cx: number, cy: number, dir: 0 | 1): PathEdge | undefined {
    const Q = this.quadOf(cx, cy, dir); if (!Q) return undefined;
    if (!this.opts.smooth) return this.finishEdge(cx, cy, dir, Q, Q.pts);
    const { a, b, qx, qy } = Q;
    // the axis the adapter gives this edge — its chord's nearer one, not the grid's (a short edge between two nodes set
    // diagonally reports the other axis), the way from a to b along it: every tangent stays within 44° of it
    const horiz = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y), ux = horiz ? Math.sign(b.x - a.x) || 1 : 0, uy = horiz ? 0 : Math.sign(b.y - a.y) || 1;
    const CAP = 44;
    const end = (N: PathNode, atB: boolean): [number, number] => {
      const hx = qx - N.x, hy = qy - N.y, h = Math.hypot(hx, hy) || 1, arrX = -hx / h, arrY = -hy / h; // (arriving at N along this edge)
      const nb = this.continuation(cx, cy, dir, atB);
      let tx = arrX, ty = arrY;
      if (nb) { const dx = nb.qx - N.x, dy = nb.qy - N.y, dl = Math.hypot(dx, dy) || 1; tx = arrX + dx / dl; ty = arrY + dy / dl; const tl = Math.hypot(tx, ty); if (tl < 1e-6) { tx = arrX; ty = arrY; } else { tx /= tl; ty /= tl; } }
      // within CAP of this edge's axis, the way it arrives at N, and of the neighbour's, the way that one leaves: the two
      // cones' overlap, the same for both edges, so the road is smooth through N. Where they do not overlap (a corner of
      // the axes, nearly square) both aim at the middle of the gap, and each stops at its own cone's edge.
      const ax = atB ? ux : -ux, ay = atB ? uy : -uy, cap = (CAP * Math.PI) / 180;
      let mid = Math.atan2(ay, ax), half = cap;
      if (nb) {
        const nh = Math.abs(nb.b.x - nb.a.x) >= Math.abs(nb.b.y - nb.a.y), away = Math.hypot(nb.a.x - N.x, nb.a.y - N.y) < 1e-6 ? 1 : -1;
        const bx = nh ? (Math.sign(nb.b.x - nb.a.x) || 1) * away : 0, by = nh ? 0 : (Math.sign(nb.b.y - nb.a.y) || 1) * away;
        const d = Math.atan2(ax * by - ay * bx, ax * bx + ay * by);
        if (Math.abs(d) <= 2 * cap) { mid += d / 2; half = cap - Math.abs(d) / 2; } else { tx = Math.cos(mid + d / 2); ty = Math.sin(mid + d / 2); } /* (apart: aim at the gap's middle, which each clamps to its own edge) */
      }
      let ang = Math.atan2(ty, tx) - mid; ang = Math.atan2(Math.sin(ang), Math.cos(ang)); if (ang > half) ang = half; else if (ang < -half) ang = -half;
      tx = Math.cos(mid + ang); ty = Math.sin(mid + ang);
      const len = (h * 2) / 3; return [N.x - tx * len, N.y - ty * len];
    };
    const [e1x, e1y] = end(a, false), [e2x, e2y] = end(b, true);
    // the middle of a cubic turns as far as its handles let it (the cone of c1−a, c2−c1, b−c2): shorter handles until the span between them is within 44° of the axis too — at none it is the chord, which is (makeQuad's cap)
    const offAxis = (dx: number, dy: number) => (Math.abs(Math.atan2(ux * dy - uy * dx, ux * dx + uy * dy)) * 180) / Math.PI; // (signed along the axis: a handle span pointing back fails)
    let c1x = e1x, c1y = e1y, c2x = e2x, c2y = e2y;
    for (const k of [1, 0.8, 0.6, 0.45, 0.3, 0.15, 0]) { c1x = a.x + (e1x - a.x) * k; c1y = a.y + (e1y - a.y) * k; c2x = b.x + (e2x - b.x) * k; c2y = b.y + (e2y - b.y) * k; if (offAxis(c2x - c1x, c2y - c1y) <= CAP) break; }
    return this.finishEdge(cx, cy, dir, Q, cubicCurve(a, c1x, c1y, c2x, c2y, b), [c1x, c1y, c2x, c2y]);
  }
  /** the edge that carries on from this one's end (`atB`: its b, else its a): the same line's next edge, or at a node with only two roads the other one */
  private continuation(cx: number, cy: number, dir: 0 | 1, atB: boolean): Quad | undefined {
    const nx = atB ? (dir === 0 ? cx + 1 : cx) : cx, ny = atB ? (dir === 1 ? cy + 1 : cy) : cy;
    const inc: [number, number, 0 | 1][] = [[nx, ny, 0], [nx, ny, 1], [nx - 1, ny, 0], [nx, ny - 1, 1]];
    const self = (c: [number, number, 0 | 1]) => c[0] === cx && c[1] === cy && c[2] === dir;
    const same = atB ? [nx, ny, dir] as [number, number, 0 | 1] : dir === 0 ? [nx - 1, ny, 0] as [number, number, 0 | 1] : [nx, ny - 1, 1] as [number, number, 0 | 1];
    const straight = this.quadOf(same[0], same[1], same[2]); if (straight) return straight;
    const others = inc.filter((c) => !self(c)).map((c) => this.quadOf(c[0], c[1], c[2])).filter((q): q is Quad => !!q);
    return others.length === 1 ? others[0] : undefined; // (a corner: this road turns there — else a T or a crossing, where each keeps its own)
  }
  /** the quadratic an edge would have (its level, class, nodes and chosen control point) or undefined when there is none, or it would be refused — memoised: the smoothing asks it of the neighbours */
  private quadOf(cx: number, cy: number, dir: 0 | 1): Quad | undefined {
    const key = cellKey(cx, cy) * 2 + dir;
    const have = this.quads.get(key); if (have !== undefined) return have ?? undefined;
    if (this.quads.size > 60000) this.quads.clear();
    const q = this.makeQuad(cx, cy, dir); this.quads.set(key, q ?? null); return q;
  }
  private quads = new Map<number, Quad | null>();
  private makeQuad(cx: number, cy: number, dir: 0 | 1): Quad | undefined {
    const level = this.levelOf(cx, cy, dir);
    if (level < 0) return undefined;
    const a = this.node(cx, cy), b = dir === 0 ? this.node(cx + 1, cy) : this.node(cx, cy + 1);
    const d = Math.max(a.density, b.density);
    const cls = this.classOf(level, d, hash01(this.seed + 227 + level, cx, cy * 2 + dir));
    // the bend: a deterministic wobble, then a lean towards whichever side is cheaper to build on
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const nx = -(b.y - a.y) / len, ny = (b.x - a.x) / len; // unit normal
    const straight = level === 0 ? 0.35 : level === 1 ? 0.7 : 1; // the bigger the road, the less it wanders for fun
    const wobble = (hash01(this.seed + 229 + level, cx, cy * 2 + dir) - 0.5) * 2 * lerp(BEND_WILD, BEND_TOWN, this.planned(d)) * straight * len;
    const band: 0 | 1 = 1; // every edge is one cell long now, so the fine grid is the right scale for all of them
    const left = this.costAt(mx + nx * LOOK, my + ny * LOOK, band);
    const right = this.costAt(mx - nx * LOOK, my - ny * LOOK, band);
    const here = this.costAt(mx, my, band);
    // how much the terrain pushes: the worse it is here against the better side, the further the curve leans.
    // A motorway shrugs it off (it would rather cut or bridge); a lane goes wherever the ground is easy.
    const engineered = lerp(1, 0.1, this.planned(d)); // a town street was surveyed and cut; a country lane followed the ground
    const stubborn = (cls === 'highway' ? 0.25 : cls === 'avenue' ? 0.55 : 1) * engineered;
    const better = Math.min(left, right);
    const push = Math.max(0, Math.min(1, (here - better) / 24)) * stubborn * len * 0.4;
    const side = left < right ? 1 : -1;
    const want = wobble + push * side;
    // Try a handful of deterministic shapes and keep the one that actually costs least to build: the lean above is a
    // guess from one sample at the midpoint, and a guess is not enough to get a road round a lake it happens to
    // straddle. Five curves scored along their length is still local, still pure, and it is the difference between a
    // road that sweeps around water and one that drives into it.
    let best: { qx: number; qy: number; pts: Float64Array; score: number } | undefined;
    // The candidates fan out by a share of the edge's *length*, and a trunk edge is 512 tiles: unbounded, that let a
    // motorway lean 160 tiles and come out diagonal, which broke the adapter's axis and continuity tests (track 2).
    // A bigger road is straighter, not bendier — the same `straight` factor that damps the wobble damps the fan, and
    // the whole lean is capped so no edge can wander far enough to stop being the axis it reports.
    const fan = len * 0.16 * straight * (level > 1 ? 0.6 : 1);
    /**
     * The hard cap, and it is a correctness rule rather than a taste one (track 2's review, item 5). A hit reports the
     * axis of the edge's *chord*, and `pos`/`off` are measured across it — so if the curve's own tangent ever crosses
     * 45° from that axis, the road is locally running the other way, and the lane maths steers the car across the
     * wrong coordinate and off the carriageway on the bend. For a quadratic Bézier the tangent is furthest from the
     * chord at the ends, at `atan(2·lean / len)`, and the chord itself already sits some angle off the axis — so what
     * is left for the lean is what the chord has not already spent. Measured before this: 17 samples in 10 766 past
     * 45°, worst 52.6°. After: none, by construction.
     */
    const chordOff = Math.abs(Math.atan2(Math.abs(b.y - a.y), Math.abs(b.x - a.x)) * 180 / Math.PI);
    const fromAxis = Math.min(chordOff, 90 - chordOff); // how far the chord already leans from the nearer axis
    const room = Math.max(0, 44 - fromAxis) * Math.PI / 180;
    const capLean = Math.min(len * 0.22 * straight, (len / 2) * Math.tan(room));
    for (let k = 0; k < 5; k++) {
      const lean = Math.max(-capLean, Math.min(capLean, want + (k - 2) * fan));
      const qx = mx + nx * lean, qy = my + ny * lean;
      const pts = curve(a, b, qx, qy);
      let score = 0;
      for (let i = 0; i <= STEPS; i++) score += this.costAt(pts[i * 2], pts[i * 2 + 1], band);
      score += Math.abs(lean) * 0.4; // a straighter road is worth something in itself
      if (!best || score < best.score) best = { qx, qy, pts, score };
    }
    const { qx, qy, pts } = best!;
    const Q: Quad = { a, b, level, cls, qx, qy, pts };
    return this.wetRefused(Q, pts) ? undefined : Q;
  }
  /** a road over too much water, or ending in it — the refusal the quadratic is judged by */
  private wetRefused(Q: Quad, pts: Float64Array): boolean {
    const sea = this.fields.seaLevel; let wet = 0;
    const wetAt = (i: number) => this.fields.elevation(pts[i * 2], pts[i * 2 + 1]) < sea;
    for (let i = 1; i < STEPS; i++) if (wetAt(i)) wet++;
    return wet > MAX_DECK[Q.cls] || Q.a.wet || Q.b.wet || wetAt(0) || wetAt(STEPS);
  }
  /** the edge from its quadratic and the curve it is drawn along (the quadratic's own, or the smoothed cubic): its bridges, its bounds */
  private finishEdge(cx: number, cy: number, dir: 0 | 1, Q: Quad, pts: Float64Array, c?: [number, number, number, number]): PathEdge | undefined {
    const { a, b, level, cls, qx, qy } = Q;
    // Water under the best curve is a **bridge**, not a missing road (owner, Q77: bridges over water are a need — until
    // they exist every lake fragments the network, invisibly in a picture and obviously the moment a car has to get
    // somewhere). Each wet run of the curve becomes a deck, padded one sample onto dry ground at either end so the
    // deck lands, not floats. What decides whether the road is built at all is how much of it would be deck: a
    // motorway spans a lake, a lane does not — a dirt track that meets water goes round or goes nowhere.
    const sea = this.fields.seaLevel;
    const wetAt: boolean[] = [];
    for (let i = 0; i <= STEPS; i++) wetAt.push(this.fields.elevation(pts[i * 2], pts[i * 2 + 1]) < sea);
    let decks: Deck[] | undefined;
    let wetTotal = 0;
    for (let i = 1; i < STEPS; i++) {
      if (!wetAt[i]) continue;
      let j = i; while (j < STEPS && wetAt[j]) j++;
      wetTotal += j - i;
      (decks ??= []).push({ t0: (i - 1) / STEPS, t1: Math.min(STEPS, j) / STEPS });
      i = j;
    }
    if (wetTotal > MAX_DECK[cls]) return undefined;
    // and the ends: a road cannot end in water, whatever it is willing to span
    if (a.wet || b.wet || wetAt[0] || wetAt[STEPS]) return undefined;
    const pad = HALF_WIDTH[cls] + 1;
    let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
    for (let i = 0; i <= STEPS; i++) {
      const px = pts[i * 2], py = pts[i * 2 + 1];
      if (px < bx0) bx0 = px; if (px > bx1) bx1 = px;
      if (py < by0) by0 = py; if (py > by1) by1 = py;
    }
    return { a, b, cls, level, qx, qy, ...(c ? { c } : {}), pts, x0: bx0 - pad, y0: by0 - pad, x1: bx1 + pad, y1: by1 + pad, key: `${cx},${cy},${dir},${level}`, ...(decks ? { decks } : {}) };
  }

  /** The two edges a cell may own, east and south. Cached. */
  /** whether a cell's edges have been generated already (the map draws what is there and generates the rest on a budget) */
  hasCell(cx: number, cy: number): boolean { return this.edges.has(cellKey(cx, cy)); }
  cellEdges(cx: number, cy: number): PathEdge[] {
    const key = cellKey(cx, cy);
    let list = this.edges.get(key);
    if (list) return list;
    if (this.edges.size > 20000) { this.edges.clear(); this.buckets.clear(); }
    list = [];
    for (const dir of [0, 1] as const) { const e = this.makeEdge(cx, cy, dir); if (e) list.push(e); }
    // (Q82 b) the railway is its own layer: a cell may own a road edge and a rail edge in the same direction
    for (const dir of [0, 1] as const) { const side = this.railSide(cx, cy, dir); if (side) list.push(this.makeRail(cx, cy, dir, side)); }
    this.edges.set(key, list);
    return list;
  }

  /**
   * (owner, Sep 23: "the big phone map is slow") A cell's edges of level `maxLevel` or bigger, and its rails — for the map
   * zoomed out. A cell already built answers from the cache; one that is not is built here *without* caching and without
   * its side streets: the far map wants a few thousand cells once, and caching them used to fill the cache past its cap,
   * which cleared the cells the game itself was driving on. A cell with no big road and no rail costs two `levelOf`s.
   */
  majorEdges(cx: number, cy: number, maxLevel: number): PathEdge[] {
    const have = this.edges.get(cellKey(cx, cy));
    if (have) return have.filter((e) => e.level <= maxLevel || e.cls === 'rail');
    const out: PathEdge[] = [];
    for (const dir of [0, 1] as const) { if (this.levelOf(cx, cy, dir, maxLevel) >= 0) { const e = this.makeEdge(cx, cy, dir); if (e) out.push(e); } }
    for (const dir of [0, 1] as const) { const side = this.railSide(cx, cy, dir); if (side) out.push(this.makeRail(cx, cy, dir, side)); }
    return out;
  }

  /** (Sep 23) where a cell's node is, near enough for the far map: the real one if it is built, else its hash and its flow without the search off water (that search is most of a node's cost) */
  private sketchNode(cx: number, cy: number): [number, number, number] {
    const key = cellKey(cx, cy), have = this.nodes.get(key); if (have) return [have.x, have.y, have.density];
    let s = this.sketchNodes.get(key); if (s) return s;
    if (this.sketchNodes.size > 80000) this.sketchNodes.clear();
    const midX = (cx + 0.5) * CELL, midY = (cy + 0.5) * CELL, dens = this.urban(midX, midY), laid = this.planned(dens), j = lerp(JITTER_WILD, JITTER_TOWN, laid) * CELL, flow = lerp(1.7, 0.06, laid) * CELL;
    s = [midX + (hash01(this.seed + 101, cx, cy) - 0.5) * 2 * j + (fbm(this.seed + 311, midX / 700, midY / 700, 2) - 0.5) * flow, midY + (hash01(this.seed + 103, cx, cy) - 0.5) * 2 * j + (fbm(this.seed + 313, midX / 700, midY / 700, 2) - 0.5) * flow, dens];
    this.sketchNodes.set(key, s); return s;
  }
  /**
   * (Sep 25, owner: Area 69 "surrounded by no roads") Could any road or railway reach into this rect? A quick, conservative answer
   * for open country that builds nothing: a remote place's probe asked the network cell by cell, and placing its nodes (the search
   * off water) cost the server a tenth of a second in each new 16 km of world. Any edge other than the trunk (level 0) — somewhere
   * built-up enough for side streets — answers yes. A trunk edge runs between two nodes, each within `NODE_SEARCH` of its sketch
   * (the node's hash and flow before the search), and bends off its chord by at most a share of its length (a quadratic's lean is
   * capped at 0.077 of it and bows half that; a smoothed cubic's handles stay within 44° of the axis — 0.4 is ample); a railway's
   * curve is exact and cheap. So an edge whose sketch stays that far plus its width from the rect cannot touch it.
   */
  roadsMayReach(x0: number, y0: number, x1: number, y1: number): boolean {
    const NODE_SEARCH = CELL * 0.22 + 0.1, bow = this.opts.smooth ? 0.4 : 0.05, PAD = 12; // (PAD: a motorway's half-width and verge, with room)
    const cx0 = Math.floor(x0 / CELL) - 3, cy0 = Math.floor(y0 / CELL) - 3, cx1 = Math.floor(x1 / CELL) + 3, cy1 = Math.floor(y1 / CELL) + 3;
    const near = (ax: number, ay: number, bx: number, by: number, r: number): boolean => { // (the segment within r of the rect)
      const X0 = x0 - r, Y0 = y0 - r, X1 = x1 + r, Y1 = y1 + r, n = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / Math.max(2, r / 2)));
      for (let k = 0; k <= n; k++) { const px = ax + ((bx - ax) * k) / n, py = ay + ((by - ay) * k) / n; if (px >= X0 && px <= X1 && py >= Y0 && py <= Y1) return true; }
      return false;
    };
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) for (const dir of [0, 1] as const) {
      const side = this.railSide(cx, cy, dir);
      if (side) { const e = this.makeRail(cx, cy, dir, side), p = e.pts; for (let i = 0; i + 3 < p.length; i += 2) if (near(p[i], p[i + 1], p[i + 2], p[i + 3], PAD)) return true; }
      const level = this.levelOf(cx, cy, dir);
      if (level < 0) continue;
      if (level > 0) return true; // (a side street's country: not the open ground this answer is for)
      const [ax, ay] = this.sketchNode(cx, cy), [bx, by] = dir === 0 ? this.sketchNode(cx + 1, cy) : this.sketchNode(cx, cy + 1);
      if (near(ax, ay, bx, by, NODE_SEARCH + bow * (Math.hypot(bx - ax, by - ay) + 2 * NODE_SEARCH) + PAD)) return true;
    }
    return false;
  }
  /** the sketch's nodes (x, y, density): two numbers and a density a cell, shared by the four edges that meet there */
  private sketchNodes = new Map<number, [number, number, number]>();
  /**
   * (Sep 23) The big roads of a cell as a sketch, for the map far out (several kilometres across, a cell a few pixels):
   * a road is the straight line between its two nodes, placed by their hash and flow without the search off water (a few
   * tiles at most, under a pixel at that scale) — and a railway is its own line's two points, exactly. What decides whether a
   * road is there is the real `levelOf`, so the sketch has every road the world has and no other; a cell already built
   * answers with its real curves. Nothing is cached: the map keeps the picture.
   */
  sketchEdges(cx: number, cy: number, maxLevel: number): { level: number; cls: PathClass; pts: ArrayLike<number>; x0: number; y0: number; x1: number; y1: number }[] {
    const have = this.edges.get(cellKey(cx, cy));
    if (have) return have.filter((e) => e.level <= maxLevel || e.cls === 'rail');
    const out: { level: number; cls: PathClass; pts: ArrayLike<number>; x0: number; y0: number; x1: number; y1: number }[] = [];
    let a0: [number, number, number] | undefined;
    for (const dir of [0, 1] as const) {
      const level = this.levelOf(cx, cy, dir, maxLevel);
      if (level >= 0) { const [ax, ay, ad] = a0 ??= this.sketchNode(cx, cy), [bx, by, bd] = dir === 0 ? this.sketchNode(cx + 1, cy) : this.sketchNode(cx, cy + 1); const cls = this.classOf(level, Math.max(ad, bd), hash01(this.seed + 227 + level, cx, cy * 2 + dir));
        // the real edge's water rule, along the straight line: no more deck than the class carries
        const sea = this.fields.seaLevel, SK = 6; let wet = 0; for (let i = 1; i < SK; i++) { const t = i / SK; if (this.fields.elevation(ax + (bx - ax) * t, ay + (by - ay) * t) < sea) wet++; } // (six steps, not fourteen: scaled to the real count below)
        if (wet * ((STEPS - 1) / (SK - 1)) <= MAX_DECK[cls]) /* (not the ends' water: a sketched node has not been moved off the shore as a real one is, and refusing on it dropped one big road piece in twelve) */ out.push({ level, cls, pts: [ax, ay, bx, by], x0: Math.min(ax, bx) - CELL / 2, y0: Math.min(ay, by) - CELL / 2, x1: Math.max(ax, bx) + CELL / 2, y1: Math.max(ay, by) + CELL / 2 }); }
      const side = this.railSide(cx, cy, dir);
      if (side) { const line = dir === 0 ? cy : cx, u0 = ((dir === 0 ? cx : cy) + 0.5) * CELL; const [px, py] = this.railLine(line, u0, dir, side), [qx, qy] = this.railLine(line, u0 + CELL, dir, side); out.push({ level: 1, cls: 'rail', pts: [px, py, qx, qy], x0: Math.min(px, qx) - CELL, y0: Math.min(py, qy) - CELL, x1: Math.max(px, qx) + CELL, y1: Math.max(py, qy) + CELL }); }
    }
    return out;
  }

  /**
   * Every edge that could reach a point. A level-L edge is owned by the cell at its west/north end and spans
   * `LEVEL_STEP[L]` cells, so the search has to look back that far — but only along the levels that step that far,
   * which is why this walks the coarse lattice rather than a big square of cells.
   */
  near(x: number, y: number): PathEdge[] {
    return this.bucket(Math.floor(x / CELL), Math.floor(y / CELL));
  }

  /**
   * The edges that overlap one cell, kept per cell. Every edge is one cell long and may stray up to a cell from its
   * own, so the 3×3 around a cell owns everything that can reach it; the bounding-box reject then leaves a handful.
   */
  bucket(cx: number, cy: number): PathEdge[] {
    const key = cellKey(cx, cy);
    let list = this.buckets.get(key);
    if (list) return list;
    if (this.buckets.size > 20000) this.buckets.clear();
    const x0 = cx * CELL, y0 = cy * CELL, x1 = x0 + CELL, y1 = y0 + CELL;
    list = [];
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        for (const e of this.cellEdges(cx + dx, cy + dy)) {
          if (e.x1 < x0 || e.x0 > x1 || e.y1 < y0 || e.y0 > y1) continue;
          list.push(e);
        }
      }
    }
    // every edge that can reach this cell is here now, so this is the moment a grade separation can be decided:
    // an edge's `overs` need the edges it crosses, and those live in other cells
    for (const e of list) if (e.overs === undefined) this.resolve(e);
    this.buckets.set(key, list);
    return list;
  }

  /**
   * Which of two crossing edges goes over the other, or neither (a level crossing, a junction). The owner's rules
   * (Q78, MAPGEN_DECISION.md §25): **rail crosses street class or better on a bridge**, only a rural lane gets a level
   * crossing. And the lattice's: a motorway goes over an avenue or a street at most crossings (`OVER_CHANCE`), never
   * over a lane (it would dead-end under the pillars); two motorways — the north–south one goes over.
   */
  private goesOver(top: PathEdge, under: PathEdge): boolean {
    if (top.cls === 'rail') return under.cls !== 'rail' && under.cls !== 'lane' && under.cls !== 'dirt';
    if (top.cls !== 'highway') return false;
    if (under.cls === 'rail' || under.cls === 'lane' || under.cls === 'dirt') return false;
    if (under.cls === 'highway') return PathNet.axisOf(top) === 'v' && PathNet.axisOf(under) === 'h';
    return hash01(this.seed + 349, top.a.cx * 2 + (top.b.cx > top.a.cx ? 0 : 1), top.a.cy * 2 + (under.a.cx + under.a.cy) % 2) < OVER_CHANCE;
  }

  /**
   * The grade separations on an edge: every other edge it crosses that it goes over, as a flat span of its parameter
   * wide enough to clear the road beneath, ramps outside it (`deckAt`). Crossings are found between the two sampled
   * polylines; an edge that merely *touches* another at a shared node is a junction, not a crossing, and is skipped.
   */
  /** what an edge is carried over, where along it, and how much of its parameter the flat part takes either side */
  private crossingsOver(e: PathEdge, all = false): { o: PathEdge; t: number; half: number; bridged: boolean }[] {
    const out: { o: PathEdge; t: number; half: number; bridged: boolean }[] = [];
    const seen = new Set<PathEdge>();
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) for (const o of this.cellEdges(e.a.cx + dx, e.a.cy + dy)) {
      if (o === e || seen.has(o) || o.cls === 'rail') continue;
      seen.add(o);
      if (o.x1 < e.x0 || o.x0 > e.x1 || o.y1 < e.y0 || o.y0 > e.y1) continue;
      if (o.a === e.a || o.a === e.b || o.b === e.a || o.b === e.b) continue; // meets at a node: a junction
      const bridged = this.goesOver(e, o);
      if (!bridged && !all) continue;
      const t = PathNet.crossing(e, o);
      if (t === undefined) continue;
      // the flat part clears the road beneath: its half-width, and the four tiles the lattice gives either side
      out.push({ o, t, half: (HALF_WIDTH[o.cls] + 4) / PathNet.length(e), bridged });
    }
    return out;
  }

  /**
   * A railway's grade separations, with the node rule: a crossing it bridges too near a node for the flat clearance
   * and a ramp runs the deck through the node (t0 = 0 / t1 = 1) for the next edge to carry on; and a track it would
   * cross at grade (a lane, a dirt road) is bridged too when it sits that near a node of an edge that is a viaduct
   * anyway — a ramp may not land on it (the adapter pulls the flat out over every road within a ramp's reach), and
   * with no room for the ramp before the node the deck has to go through the node instead.
   */
  private railOvers(e: PathEdge): { t0: number; t1: number; under: PathEdge; /** how far the flat part overruns each node, in this edge's parameter (0 = it fits) */ pastA: number; pastB: number }[] {
    const room = (OVER_FLAT + OVER_RAMP) / PathNet.length(e);
    const all = this.crossingsOver(e, true), viaduct = all.some((c) => c.bridged);
    const out: { t0: number; t1: number; under: PathEdge; pastA: number; pastB: number }[] = [];
    // (bug report, Sep 12: "the railroad stops in the air") the deck may run through a node only where another rail edge
    // carries it on; at the line's end there is nothing to ramp down on, so a crossing that near a terminus is at grade
    const onA = this.railGoesOn(e, e.a), onB = this.railGoesOn(e, e.b);
    for (const { o, t, half, bridged } of all) {
      const nearA = t - half < room, nearB = 1 - t - half < room;
      if ((nearA && !onA) || (nearB && !onB)) continue;
      if (!bridged && !(viaduct && (nearA || nearB))) continue;
      out.push({ t0: nearA ? 0 : t - half, t1: nearB ? 1 : t + half, under: o, pastA: Math.max(0, half - t), pastB: Math.max(0, t + half - 1) });
    }
    return out;
  }

  /** does another rail edge leave this node? (a rail edge makes its own node objects: the shared node is a place) */
  private railGoesOn(e: PathEdge, end: PathNode): boolean {
    const same = (n: PathNode): boolean => Math.abs(n.x - end.x) < 1e-6 && Math.abs(n.y - end.y) < 1e-6;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) for (const k of this.cellEdges(end.cx + dx, end.cy + dy)) if (k !== e && k.cls === 'rail' && (same(k.a) || same(k.b))) return true;
    return false;
  }

  private resolve(e: PathEdge): void {
    const overs: Over[] = [];
    if (e.cls === 'rail' || e.cls === 'highway') {
      // (a railway's crossing too near a node for the flat clearance and a ramp: the deck runs through the node — this end
      // flat to the node, the next edge carrying it on and ramping down there, see below)
      if (e.cls === 'rail') overs.push(...this.railOvers(e));
      else for (const { o, t, half } of this.crossingsOver(e)) overs.push({ t0: Math.max(0, t - half), t1: Math.min(1, t + half), under: o });
      // a railway crossing a road just past one of its own nodes: the viaduct runs on through the node onto this edge
      // (owner, Sep 10 night: a train "stuck" where its deck began at a node as a cliff, and a level crossing that was
      // half a viaduct) — the next rail edge's crossing, within its flat part's reach of the shared node, is carried
      // over here for the rest of that reach, so the two edges' decks meet at the node at height
      if (e.cls === 'rail') for (const end of [e.a, e.b]) {
        const same = (n: PathNode): boolean => Math.abs(n.x - end.x) < 1e-6 && Math.abs(n.y - end.y) < 1e-6; // (a rail edge makes its own node objects: the shared node is a place)
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) for (const k of this.cellEdges(end.cx + dx, end.cy + dy)) {
          if (k === e || k.cls !== 'rail' || (!same(k.a) && !same(k.b))) continue;
          for (const o of this.railOvers(k)) {
            const through = same(k.a) ? o.t0 === 0 : o.t1 === 1; // k's deck runs through the shared node: this edge carries it on
            if (!through) continue;
            // the flat part k could not fit before its node (at least one row of it here), and the ramp down after it
            const past = Math.max(1 / PathNet.length(e), ((same(k.a) ? o.pastA : o.pastB) * PathNet.length(k)) / PathNet.length(e));
            overs.push(end === e.a ? { t0: 0, t1: Math.min(1, past), under: o.under } : { t0: Math.max(0, 1 - past), t1: 1, under: o.under });
          }
        }
      }
      // and the interchanges: a trunk edge whose end node carries it over the crossing road gets a deck at that end,
      // wide enough to clear the widest arm beneath, ramping down along the edge
      if (e.cls === 'highway' && e.level === 0) {
        const len = PathNet.length(e);
        for (const end of [e.a, e.b]) {
          const j = this.junctionAt(end.cx, end.cy);
          if (!j || !j.over || j.over !== PathNet.axisOf(e)) continue;
          const under = j.arms.filter((a) => a.axis !== j.over).map((a) => a.edge);
          if (!under.length) continue;
          const widest = under.reduce((w, u) => Math.max(w, HALF_WIDTH[u.cls]), 0);
          const half = (widest + 4) / len;
          overs.push(end === e.a ? { t0: 0, t1: Math.min(1, half), under: under[0] } : { t0: Math.max(0, 1 - half), t1: 1, under: under[0] });
        }
      }
      overs.sort((p, q) => p.t0 - q.t0);
    }
    e.overs = overs;
  }

  /** The edge's grade separations, resolved now if `bucket()` has not been past it yet (the adapter asks before the deck rows). */
  oversOf(e: PathEdge): Over[] {
    if (e.overs === undefined) this.resolve(e);
    return e.overs!;
  }

  /** the length of the sampled curve, tiles */
  static length(e: PathEdge): number {
    let l = 0;
    for (let i = 0; i < STEPS; i++) l += Math.hypot(e.pts[i * 2 + 2] - e.pts[i * 2], e.pts[i * 2 + 3] - e.pts[i * 2 + 1]);
    return l;
  }

  /** where two sampled curves cross, as a parameter of the first — or undefined when they do not */
  static crossing(e: PathEdge, o: PathEdge): number | undefined {
    const p = e.pts, q = o.pts;
    for (let i = 0; i < STEPS; i++) {
      const ax = p[i * 2], ay = p[i * 2 + 1], bx = p[i * 2 + 2], by = p[i * 2 + 3];
      const rx = bx - ax, ry = by - ay;
      for (let j = 0; j < STEPS; j++) {
        const cx = q[j * 2], cy = q[j * 2 + 1], dx = q[j * 2 + 2], dy = q[j * 2 + 3];
        const sx = dx - cx, sy = dy - cy;
        const den = rx * sy - ry * sx;
        if (Math.abs(den) < 1e-9) continue;
        const t = ((cx - ax) * sy - (cy - ay) * sx) / den;
        const u = ((cx - ax) * ry - (cy - ay) * rx) / den;
        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return (i + t) / STEPS;
      }
    }
    return undefined;
  }

  /**
   * The deck height over a tile — the lattice's `overpassH`, re-hung on edges (MAPGEN_DECISION.md §6): `OVER_H` on the
   * flat span of a grade separation, a ramp of `OVER_RAMP` tiles down to the ground either side, 0 elsewhere. A bridge
   * over water is *not* a deck in this sense (it is at grade, the way the lattice's level-0 lines cross water); ask
   * `roadAt(...).deck` for that. The edge on the deck comes back with the height, for the generator's deck layer.
   */
  deckAt(x: number, y: number): { h: number; edge: PathEdge; t: number } | undefined {
    let best: { h: number; edge: PathEdge; t: number } | undefined;
    for (const e of this.near(x, y)) {
      if (!e.overs?.length) continue;
      const w = HALF_WIDTH[e.cls] + 1;
      if (x < e.x0 - w || x > e.x1 + w || y < e.y0 - w || y > e.y1 + w) continue;
      const hit = PathNet.project(e, x, y);
      if (hit.d > w) continue;
      const len = PathNet.length(e), ramp = OVER_RAMP / len;
      let h = 0;
      for (const o of e.overs) {
        if (hit.t >= o.t0 && hit.t <= o.t1) { h = OVER_H; break; }
        const out = hit.t < o.t0 ? o.t0 - hit.t : hit.t - o.t1;
        if (out < ramp) h = Math.max(h, Math.round(OVER_H * (1 - out / ramp) * 100) / 100);
      }
      if (h > 0 && (!best || h > best.h)) best = { h, edge: e, t: hit.t };
    }
    return best;
  }

  /** the nearest point of one edge's curve to (x, y): distance and parameter */
  static project(e: PathEdge, x: number, y: number): { d: number; t: number } {
    const p = e.pts;
    let bd = Infinity, bt = 0;
    for (let i = 0; i < STEPS; i++) {
      const ax = p[i * 2], ay = p[i * 2 + 1], bx = p[i * 2 + 2], by = p[i * 2 + 3];
      const vx = bx - ax, vy = by - ay, len2 = vx * vx + vy * vy || 1;
      let t = ((x - ax) * vx + (y - ay) * vy) / len2;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const d = Math.hypot(x - (ax + vx * t), y - (ay + vy * t));
      if (d < bd) { bd = d; bt = (i + t) / STEPS; }
    }
    return { d: bd, t: bt };
  }

  /** The railway under a tile, if any: the hit, whether it is on a viaduct here, and whether it is carried over a road here. */
  railAt(x: number, y: number): { edge: PathEdge; t: number; d: number; deck: boolean; over: boolean } | undefined {
    const hit = this.nearest(x, y, 4, true);
    if (!hit || hit.d > HALF_WIDTH.rail) return undefined;
    const over = !!hit.edge.overs?.some((o) => hit.t >= o.t0 && hit.t <= o.t1);
    return { edge: hit.edge, t: hit.t, d: hit.d, deck: PathNet.onDeck(hit.edge, hit.t), over };
  }

  /** The nearest road centre line to a point, if one is within `max` tiles. */
  nearest(x: number, y: number, max = 24, rail = false): PathHit | undefined {
    let best: PathHit | undefined;
    for (const e of this.near(x, y)) {
      if ((e.cls === 'rail') !== rail) continue; // roads and rails are two layers: a train is never on a road, a car never on a track
      if (x < e.x0 - max || x > e.x1 + max || y < e.y0 - max || y > e.y1 + max) continue;
      const p = e.pts;
      for (let i = 0; i < STEPS; i++) {
        const ax = p[i * 2], ay = p[i * 2 + 1], bx = p[i * 2 + 2], by = p[i * 2 + 3];
        const vx = bx - ax, vy = by - ay, len2 = vx * vx + vy * vy || 1;
        let t = ((x - ax) * vx + (y - ay) * vy) / len2;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const d = Math.hypot(x - (ax + vx * t), y - (ay + vy * t));
        if (d < max && (!best || d < best.d)) best = { edge: e, d, t: (i + t) / STEPS };
      }
    }
    return best;
  }

  /** the axis an edge reports — the nearer of the two to its chord, the same rule the adapter uses for `RoadHit.axis` */
  static axisOf(e: PathEdge): 'v' | 'h' { return Math.abs(e.b.x - e.a.x) >= Math.abs(e.b.y - e.a.y) ? 'h' : 'v'; }

  /**
   * The junction at a node, if any road meets there: its arms are the node's own east and south edges at every level
   * it carries, plus the west and north edges owned by the neighbouring cells that step onto it. Two arms is a bend in
   * one road; three is a T (the shape the lattice never had); four a crossing. Nothing here is new geometry — it is
   * the edges that already exist, read from the point where they meet, which is why it is cheap.
   */
  junctionAt(cx: number, cy: number): Junction | undefined {
    const n = this.node(cx, cy);
    if (n.wet) return undefined;
    const arms: Arm[] = [];
    // a railway is not an arm: it has its own nodes, and where it crosses a road it is a bridge or a level crossing, not a junction
    for (const e of this.cellEdges(cx, cy)) if (e.cls !== 'rail') arms.push({ edge: e, away: 1, axis: PathNet.axisOf(e) });
    for (const e of this.cellEdges(cx - 1, cy)) if (e.cls !== 'rail' && e.b === n) arms.push({ edge: e, away: -1, axis: PathNet.axisOf(e) });
    for (const e of this.cellEdges(cx, cy - 1)) if (e.cls !== 'rail' && e.b === n) arms.push({ edge: e, away: -1, axis: PathNet.axisOf(e) });
    if (!arms.length) return undefined;
    const j: Junction = { x: n.x, y: n.y, cx, cy, arms };
    const over = this.interchange(j);
    if (over) j.over = over;
    return j;
  }

  /**
   * Is this junction an interchange, and which axis goes over? The rule, from plan B's "motorway interchanges
   * kilometres apart" and the lattice's `overpassH`: a trunk road (level 0) that passes straight through the node —
   * two highway arms, opposite — crossing a road of avenue or street class on the other axis, at a node on the
   * kilometre grid along the trunk, most of the time. Never over a lane (it would dead-end under the pillars), and
   * never over another trunk — two motorways meeting stay at grade until slip roads exist to join them. A junction
   * object is the right place for this decision — on the path net two roads only ever meet at nodes, so a grade
   * separation *is* a property of the node (§29).
   */
  private interchange(j: Junction): 'v' | 'h' | undefined {
    if (j.arms.length !== 4) return undefined;
    const hw = j.arms.filter((a) => a.edge.cls === 'highway');
    // exactly two trunk arms, opposite: a trunk meeting another trunk stays at grade until there are slip roads to
    // join them — an overpass with no way on or off would cut the network where it most needs to connect
    if (hw.length !== 2 || hw[0].axis !== hw[1].axis) return undefined;
    const over = hw[0].axis;
    const along = over === 'h' ? j.cx : j.cy;
    // on the kilometre grid, offset so the crossing is a level-1 line (a town's avenue or street), not another trunk
    if (((along % INTERCHANGE_EVERY) + INTERCHANGE_EVERY) % INTERCHANGE_EVERY !== INTERCHANGE_AT) return undefined;
    const cross = j.arms.filter((a) => a.axis !== over);
    if (cross.length !== 2) return undefined;
    if (cross.some((a) => a.edge.cls !== 'avenue' && a.edge.cls !== 'street')) return undefined;
    if (hash01(this.seed + 353, j.cx, j.cy) >= INTERCHANGE_CHANCE) return undefined;
    return over;
  }

  /**
   * A node that is not a junction: exactly two arms, running on through it. On a chain (a motorway is a chain of
   * 64-tile edges) nearly every node is one of these, and anything that lays things out *along a road* — lots, kerbs,
   * names — should read straight through it rather than treating it as a corner. Track 2's lot planner boxed every
   * node as a junction and lost end lots at every pass-through when a road widened (0.89 → 0.82 in place).
   */
  passThrough(j: Junction): boolean {
    if (j.arms.length !== 2) return false;
    // the two arms leave in roughly opposite directions: not a bend sharp enough to be a corner
    const dir = (a: Arm): [number, number] => { const e = a.edge, q = e.c ? (a.away === 1 ? { x: e.c[0], y: e.c[1] } : { x: e.c[2], y: e.c[3] }) : { x: e.qx, y: e.qy }; const from = a.away === 1 ? e.a : e.b; const dx = q.x - from.x, dy = q.y - from.y, l = Math.hypot(dx, dy) || 1; return [dx / l, dy / l]; };
    const [ax, ay] = dir(j.arms[0]), [bx, by] = dir(j.arms[1]);
    return ax * bx + ay * by < -0.5; // more than 120° apart: the road carries on
  }

  /** Every junction in a box of world coordinates — the renderer's viewbox, a camera's cell. */
  junctionsIn(x0: number, y0: number, x1: number, y1: number, out: Junction[] = []): Junction[] {
    out.length = 0;
    // a node may sit up to (JITTER_WILD + flow) cells outside its own cell, so the cell walk pads by two
    const c0 = Math.floor(x0 / CELL) - 2, c1 = Math.ceil(x1 / CELL) + 2;
    const r0 = Math.floor(y0 / CELL) - 2, r1 = Math.ceil(y1 / CELL) + 2;
    for (let cy = r0; cy <= r1; cy++) {
      for (let cx = c0; cx <= c1; cx++) {
        const j = this.junctionAt(cx, cy);
        if (j && j.x >= x0 && j.x <= x1 && j.y >= y0 && j.y <= y1) out.push(j);
      }
    }
    return out;
  }

  /**
   * The identity of the **road** an edge belongs to — the run of one level's edges along one cell line, from where
   * the line starts being claimed. Every edge of the run answers the same key, and the key is a pure function of the
   * seed: it walks back along the line through the same cached `levelOf` everything else uses (capped at `RUN_MAX`
   * cells, so a very long road changes name now and then, the way a real one does). A trunk route keeps one key for
   * its whole line, and a railway for its whole line too.
   */
  roadKey(e: PathEdge): string {
    const hit = this.roadKeys.get(e.key);
    if (hit) return hit;
    if (this.roadKeys.size > 20000) this.roadKeys.clear();
    const dir: 0 | 1 = e.b.cx > e.a.cx ? 0 : 1;
    const line = dir === 0 ? e.a.cy : e.a.cx;
    let key: string;
    if (e.cls === 'rail') key = `${dir},${line},rail`;
    else if (e.level === 0) key = `${dir},${line},0,line`;
    else {
      let along = dir === 0 ? e.a.cx : e.a.cy;
      for (let n = 0; n < RUN_MAX; n++) {
        const px = dir === 0 ? along - 1 : e.a.cx, py = dir === 0 ? e.a.cy : along - 1;
        if (this.levelOf(px, py, dir) !== e.level) break;
        along--;
      }
      key = `${dir},${line},${e.level},${along}`;
    }
    this.roadKeys.set(e.key, key);
    return key;
  }

  /** The name of the road an edge is part of (content/names, kind `street`): the same for every edge of the run. */
  nameOf(e: PathEdge): string {
    this.names ??= new StreetNames(this.fields.reg, this.seed, this.fields.settlements);
    return this.names.name(this.roadKey(e), e.cls, (e.a.x + e.b.x) / 2, (e.a.y + e.b.y) / 2);
  }

  /**
   * **Piece attachment** (MAPGEN_DECISION.md §16 item 3 — on the critical path): a player's street is a straight
   * 16-tile piece on the 16-grid (`overlay.ts`), and on the lattice its end either sat on a line or it did not. A
   * generated road is a curve now, so a piece's end lands a few tiles short of the kerb, or a few past it, and the
   * question "does this end touch a road" has a distance in it. This answers it: from the end (pos, e) of a piece on
   * `axis`, looking `toward` (+1 = increasing coordinate, −1 = decreasing), the first tile of a road's carriageway
   * within `REACH` tiles along the piece's own line — `gap` tiles to pave from the end to the kerb (0 = the end is
   * already on the road), and the road it reaches. `undefined` = nothing to attach to that way. A railway is never
   * something to attach to. The generator paves the `gap` tiles in the piece's own road, so the piece and the curve
   * meet at a kerb rather than a gap; `endTouches` asks for either direction.
   */
  attachment(axis: 'v' | 'h', pos: number, e: number, toward: 1 | -1): { gap: number; edge: PathEdge; cls: PathClass } | undefined {
    for (let gap = 0; gap <= ATTACH_REACH; gap++) {
      const u = e + toward * gap;
      const x = axis === 'v' ? pos + 0.5 : u + 0.5, y = axis === 'v' ? u + 0.5 : pos + 0.5;
      const hit = this.nearest(x, y, 9);
      if (!hit || hit.d > HALF_WIDTH[hit.edge.cls]) continue;
      return { gap, edge: hit.edge, cls: hit.edge.cls };
    }
    return undefined;
  }

  /** Is this parameter of the edge on a deck? (a car up there is at deck height, and the water under it does not stop it) */
  static onDeck(e: PathEdge, t: number): boolean {
    if (!e.decks) return false;
    for (const d of e.decks) if (t >= d.t0 && t <= d.t1) return true;
    return false;
  }

  /** Is this point on a road, and which? (the query the chunk generator would ask; a railway is `railAt`, not a road) */
  roadAt(x: number, y: number): { cls: PathClass; /** 0 at the kerb, 1 at the centre line */ across: number; /** carried over water here */ deck: boolean } | undefined {
    const hit = this.nearest(x, y, 9);
    if (!hit) return undefined;
    const w = HALF_WIDTH[hit.edge.cls];
    if (hit.d > w) return undefined;
    return { cls: hit.edge.cls, across: 1 - hit.d / w, deck: PathNet.onDeck(hit.edge, hit.t) };
  }
}
